import { useState } from "react"
import { AlertCircle, Download, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { downloadCSV, toCSV, todayIso } from "@/lib/csv"

/**
 * ExportButton — reusable CSV export trigger for any list page.
 *
 * Behavior:
 *   1. Click → fetch all pages via the provided `request` function
 *      (paginating with `pageSize` rows at a time)
 *   2. Map each row through `columns[].get` to build a flat row object
 *   3. Serialize to CSV (RFC 4180) and trigger a browser download
 *
 * Why fetch-all-pages client-side instead of a backend `?export=true` flag:
 *   This batch is intentionally frontend-only. For very large datasets
 *   (10k+ rows) a backend export endpoint would be more efficient. Flagged
 *   in docs/ADMIN_REDESIGN.md as a future optimization.
 *
 * Errors are shown inline via a small alert below the button (auto-clears
 * after 6s).
 */

export type ExportColumn<T> = {
  /** Column header in the CSV (also serves as the field key). */
  label: string
  /** Extract the export value from a row. Return null/undefined for empty. */
  get: (row: T) => string | number | boolean | null | undefined
}

export type PaginatedListResponse<T> = {
  docs: T[]
  totalDocs: number
}

export type ExportButtonProps<T> = {
  /** Display label on the button. Defaults to "Export". */
  label?: string
  /**
   * Filename prefix. The current date and `.csv` are appended automatically.
   * Example: `"users"` → `users-2026-04-17.csv`.
   */
  filenamePrefix: string
  columns: ExportColumn<T>[]
  /**
   * Fetch a single page of data with the provided pagination params.
   * Should already include any active search / sort / filter from the page.
   */
  request: (page: number, limit: number) => Promise<PaginatedListResponse<T>>
  /** Page size used while paginating. Default 500 — balances round-trips vs response size. */
  pageSize?: number
  /** Maximum rows allowed per export. Safety cap. Default 50,000. */
  maxRows?: number
  /** Optional total estimate (e.g. from the current list query) to show in the progress label. */
  totalEstimate?: number
  /** Disable the button externally (e.g. while another action is in flight). */
  disabled?: boolean
}

export function ExportButton<T>({
  label = "Export CSV",
  filenamePrefix,
  columns,
  request,
  pageSize = 500,
  maxRows = 50_000,
  totalEstimate,
  disabled,
}: ExportButtonProps<T>) {
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const onClick = async () => {
    setError(null)
    setProgress(0)
    setIsExporting(true)
    const all: T[] = []
    try {
      let page = 1
      // Loop until the server returns less than a full page (or we hit cap).
      while (true) {
        const resp = await request(page, pageSize)
        all.push(...resp.docs)
        setProgress(all.length)
        if (resp.docs.length < pageSize) break
        if (resp.totalDocs && all.length >= resp.totalDocs) break
        if (all.length >= maxRows) {
          setError(
            `Capped at ${maxRows.toLocaleString()} rows. Refine filters or contact engineering for a backend export.`
          )
          break
        }
        page++
      }

      // Build row objects keyed by column label, then serialize.
      const rowObjs = all.map((row) => {
        const o: Record<string, unknown> = {}
        for (const c of columns) {
          const v = c.get(row)
          o[c.label] = v == null ? "" : v
        }
        return o
      })
      const headers = columns.map((c) => ({ key: c.label, label: c.label }))
      const csv = toCSV(headers, rowObjs)
      downloadCSV(`${filenamePrefix}-${todayIso()}.csv`, csv)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed.")
    } finally {
      setIsExporting(false)
      // Auto-clear error after a moment so it doesn't linger forever
      if (error) setTimeout(() => setError(null), 6000)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="outline"
        onClick={onClick}
        disabled={disabled || isExporting}
        title={
          totalEstimate && totalEstimate > 0
            ? `Export ~${totalEstimate.toLocaleString()} rows as CSV`
            : "Export current results as CSV"
        }
      >
        {isExporting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Download className="mr-2 h-4 w-4" />
        )}
        {isExporting
          ? progress > 0
            ? `Exporting ${progress.toLocaleString()}…`
            : "Exporting…"
          : label}
      </Button>
      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-xs text-destructive"
        >
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
